(function (global) {
  'use strict';
  const invalid = (reason) => ({valid: false, requestOnly: false, priceCents: null, variantId: null, money: null, reason});
  const normalizeHex = (value) => {
    const code = String(value || '').trim().replace(/^#/, '');
    return /^[0-9a-f]{6}$/i.test(code) ? '#' + code.toUpperCase() : null;
  };
  const parseDimension = (value) => {
    const text = String(value ?? '').trim();
    if (!/^(?:\d+(?:[.,]\d*)?|[.,]\d+)$/.test(text)) return NaN;
    const number = Number(text.replace(',', '.'));
    return Number.isFinite(number) ? number : NaN;
  };
  function quote(product, width, height, baseVariantId, mountingKey, selections) {
    if (!product || typeof width !== 'number' || typeof height !== 'number'
      || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return invalid('dimensions');
    const bounds = product.bounds || {};
    if ((typeof bounds.width_min_cm === 'number' && width < bounds.width_min_cm)
      || (typeof bounds.width_max_cm === 'number' && width > bounds.width_max_cm)
      || (typeof bounds.height_min_cm === 'number' && height < bounds.height_min_cm)
      || (typeof bounds.height_max_cm === 'number' && height > bounds.height_max_cm)
      || (product.kind === 'pleated' && mountingKey === 'glue' && width > 100)) return invalid('dimensions');
    if (product.priceMode === 'inquiry') return {valid: true, requestOnly: true, priceCents: null, variantId: null, money: null, reason: 'pricing'};
    let variantId = String(baseVariantId);
    const selectedOptions = Object.assign({}, selections || {}, {mounting: mountingKey});
    const effectiveConfig = global.NSProductOptions?.restrictToProduct(product.optionsConfig, product.handle) || product.optionsConfig;
    const options = effectiveConfig ? global.NSProductOptions?.resolve(effectiveConfig, selectedOptions, {width, height}) : null;
    if (product.optionsConfig && (!options || options.errors.length > 0)) return invalid('options');
    if (product.priceMode === 'measure') {
      const resolved = global.NSMeasurePricing?.resolve(product.pricing, width, height, baseVariantId, options?.surchargeCents || 0);
      if (!resolved?.valid) return invalid('pricing');
      variantId = String(resolved.variantId);
    } else if (product.priceMode !== 'fixed') return invalid('pricing');
    const variants = global.NSMeasurePricing?.completeVariants(product.pricing, product.variants) || product.variants || [];
    const variant = variants.find((entry) => String(entry.id) === variantId);
    if (!variant || variant.available !== true || !Number.isSafeInteger(variant.price) || variant.price < 0 || !variant.money) return invalid('unavailable');
    return {valid: true, requestOnly: false, priceCents: variant.price, variantId, money: variant.money, reason: null};
  }
  global.NS3DConfigurator = {quote, normalizeHex, parseDimension};
  const instances = new WeakMap();
  function initialize(root) {
    if (instances.has(root) || !root.querySelector('[data-ns-3d-form]')) return;
    const find = (name) => root.querySelector('[data-ns-3d-' + name + ']');
    const labels = JSON.parse(find('labels').textContent);
    const products = [...root.querySelectorAll('[data-ns-3d-product]')].map((node) => JSON.parse(node.textContent));
    const productByHandle = new Map(products.map((product) => [product.handle, product]));
    const form = find('form'), select = find('select'), widthInput = find('width'), heightInput = find('height');
    const mounting = find('mounting'), colorsHost = find('colors'), optionsHost = find('options'), addButton = find('add'), inquiryButton = find('inquiry');
    const sceneHost = find('scene'), sceneStatus = find('scene-status');
    let scene;
    const syncSceneStatus = () => {
      const unavailable = !scene || sceneHost.dataset.ns3dMode === 'unavailable';
      const rendered = sceneHost.hasAttribute('data-ns3d-render-ms');
      sceneHost.setAttribute('aria-busy', String(!unavailable && !rendered));
      if (sceneStatus) {
        sceneStatus.hidden = !unavailable && rendered;
        if (unavailable) sceneStatus.textContent = labels.sceneUnavailable;
      }
    };
    try { scene = global.NS3DScene?.create(sceneHost); } catch (error) { scene = null; }
    const sceneObserver = typeof MutationObserver === 'function' ? new MutationObserver(syncSceneStatus) : null;
    sceneObserver?.observe(sceneHost, {attributes: true, attributeFilter: ['data-ns3d-mode', 'data-ns3d-render-ms']});
    syncSceneStatus();
    instances.set(root, {destroy() { sceneObserver?.disconnect(); scene?.destroy(); }});
    const saved = new Map();
    let product, state, currentQuote, resolvedOptions, optionsConfig, busy = false, placement = 'room', configurationKey = '';
    let photoMatrix = null;
    const colorTitle = (value) => value.label || value.name;
    const numberText = (number) => new Intl.NumberFormat(root.dataset.locale || 'de', {maximumFractionDigits: 8}).format(number);
    const element = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const chosen = (group, index) => group.values?.[state.choices[index]];
    const selectionText = (value, index) => value?.consultation
      ? [labels.hexCustom, normalizeHex(state.hex[index])].filter(Boolean).join(' ')
      : value ? colorTitle(value) : '';
    const previewSelection = element('dl', 'ns3d-preview-selection');
    previewSelection.setAttribute('data-ns-3d-selection', '');
    previewSelection.setAttribute('aria-live', 'polite');
    previewSelection.setAttribute('aria-atomic', 'true');
    previewSelection.hidden = true;
    sceneHost.after(previewSelection);
    function properties() {
      const result = {'Breite (cm)': String(parseDimension(widthInput.value)), 'Höhe (cm)': String(parseDimension(heightInput.value))};
      if (!optionsConfig?.groups.some((group) => group.key === 'mounting')) result.Montage = mounting.selectedOptions[0]?.textContent || '';
      (product.groups || []).forEach((group, index) => {
        const value = chosen(group, index);
        if (!value || group.kind !== 'property') return;
        result[group.property_name || group.option_name] = value.consultation ? labels.hexCustom : value.name;
        if (value.consultation) result['HEX-Code'] = normalizeHex(state.hex[index]) || '';
      });
      Object.assign(result, resolvedOptions?.properties || {});
      return result;
    }
    function baseId() {
      let id = product.defaultVariantId;
      (product.groups || []).forEach((group, index) => { if (group.kind === 'variant') id = chosen(group, index)?.variant_id || id; });
      return id;
    }
    function renderScene() {
      let frameColor = '#F1F0EA', fabricColor = product.kind === 'wood' ? '#AE8050' : '#D2C4AE', textureUrl = null;
      (product.groups || []).forEach((group, index) => {
        const value = chosen(group, index);
        if (!value) return;
        if (['Rahmenfarbe', 'Akssesuar', 'Profilfarbe'].includes(group.option_name)) {
          frameColor = value.consultation ? normalizeHex(state.hex[index]) || '#F1F0EA'
            : value.color || (/schwarz/i.test(value.name) ? '#242424' : /anthrazit/i.test(value.name) ? '#383E42' : '#F1F0EA');
        } else {
          fabricColor = value.color || fabricColor;
          textureUrl = value.image_url || null;
        }
      });
      const selected = resolvedOptions?.selected || state.options;
      if (product.kind === 'pleated') {
        const structure = selected.fabric_structure || 'pleated';
        colorsHost.querySelectorAll('[data-ns3d-fabric-image]').forEach((image) => {
          const group = product.groups[Number(image.dataset.ns3dFabricGroup)];
          const value = group?.values[Number(image.dataset.ns3dFabricImage)];
          const source = value?.media_by_structure?.[structure]?.url || value?.image_url;
          if (source && image.getAttribute('src') !== source) image.src = source;
        });
      }
      const frameProfile = selected.frame_profile === 'standard_35' ? 35 : selected.frame_profile?.startsWith('low_9') ? 9 : product.profileWidthMm;
      const sceneKind = product.kind === 'screen-single' && selected.wing_count === 'double' ? 'screen-double' : product.kind;
      scene?.set({kind: sceneKind, width: state.previewWidth || product.defaults.width, height: state.previewHeight || product.defaults.height,
        frameColor, fabricColor, textureUrl, opacity: product.opacity, opening: Number(find('opening').value), mounting: mounting.value,
        fabricStructure: selected.fabric_structure || 'pleated', operatingDirection: selected.operating_direction, openingDirection: selected.opening_direction,
        slatWidthMm: 50, profileWidthMm: frameProfile, placement});
      const fabricGroup = (product.groups || []).findIndex((group) => group.option_name === 'Stofffarbe');
      const fabricValue = fabricGroup >= 0 ? chosen(product.groups[fabricGroup], fabricGroup) : null;
      const frameGroup = (product.groups || []).findIndex((group) => ['Rahmenfarbe', 'Akssesuar', 'Profilfarbe'].includes(group.option_name));
      const frameValue = frameGroup >= 0 ? chosen(product.groups[frameGroup], frameGroup) : null;
      const patternGroup = optionsConfig?.groups.find((group) => group.key === 'fabric_structure');
      const patternValue = patternGroup?.values.find((value) => value.id === (selected.fabric_structure || 'pleated'));
      const selectionRows = [];
      if (fabricValue) selectionRows.push([product.groups[fabricGroup].label || product.groups[fabricGroup].option_name, selectionText(fabricValue, fabricGroup)]);
      if (frameValue) selectionRows.push([product.groups[frameGroup].label || product.groups[frameGroup].option_name, selectionText(frameValue, frameGroup)]);
      if (patternValue) selectionRows.push([patternGroup.label, patternValue.label]);
      previewSelection.hidden = product.kind !== 'pleated' || !selectionRows.length;
      previewSelection.replaceChildren();
      selectionRows.forEach(([key, value]) => {
        const item = element('div');
        item.append(element('dt', '', key), element('dd', '', value));
        previewSelection.append(item);
      });
      const pickerImage = find('picker-image');
      if (pickerImage && product.kind === 'pleated') {
        const structure = selected.fabric_structure || 'pleated';
        const photo = !fabricValue?.consultation && !frameValue?.consultation
          ? photoMatrix?.get(fabricValue?.name, frameValue?.name, structure) : null;
        const media = photo || (/^(weiß|weiss|white)$/i.test(frameValue?.name || '')
          ? fabricValue?.media_by_structure?.[structure] : null);
        if (photo) global.NSPlisseePhotos.setImage(pickerImage, photo, true);
        else if (media?.url) {
          pickerImage.removeAttribute('srcset'); pickerImage.src = media.url;
          pickerImage.alt = [product.title, ...selectionRows.map(([key, value]) => key + ': ' + value)].join(', ');
        }
        pickerImage.hidden = !media?.url || !!find('picker')?.querySelector('[data-ns-3d-picker-current]')?.classList.contains('ns3d-image-unavailable');
      }
      const roomNote = find('room-note'); if (roomNote) roomNote.hidden = placement !== 'room';
      const w = numberText(state.previewWidth || product.defaults.width), h = numberText(state.previewHeight || product.defaults.height);
      find('width-label').textContent = w + ' cm'; find('height-label').textContent = h + ' cm';
      find('canvas').setAttribute('aria-label', [product.title, labels.width + ' ' + w + ' cm', labels.height + ' ' + h + ' cm',
        ...selectionRows.map(([key, value]) => key + ': ' + value)].join(', '));
    }
    function update() {
      state.width = widthInput.value; state.height = heightInput.value; state.mounting = mounting.value;
      state.options.mounting = mounting.value;
      const width = parseDimension(widthInput.value), height = parseDimension(heightInput.value);
      resolvedOptions = product.optionsConfig ? global.NSProductOptions?.resolve(product.optionsConfig, state.options, {width, height}) : null;
      currentQuote = quote(product, width, height, baseId(), mounting.value, state.options);
      const customGroups = (product.groups || []).filter((group, index) => chosen(group, index)?.consultation);
      const hexInvalid = (product.groups || []).some((group, index) => chosen(group, index)?.consultation && !normalizeHex(state.hex[index]));
      const requestOnly = currentQuote.requestOnly || resolvedOptions?.inquiryRequired || customGroups.length > 0 || root.dataset.customOrders !== 'true';
      const glue = product.kind === 'pleated' && mounting.value === 'glue';
      find('glue-note').hidden = !glue;
      const widthMax = glue ? Math.min(100, product.bounds.width_max_cm || 100) : product.bounds.width_max_cm;
      const range = (min, max) => max == null ? '≥ ' + numberText(min) + ' cm' : numberText(min) + ' – ' + numberText(max) + ' cm';
      find('width-bounds').textContent = range(product.bounds.width_min_cm, widthMax);
      find('height-bounds').textContent = range(product.bounds.height_min_cm, product.bounds.height_max_cm);
      const validateDimension = (input, value, min, max, label) => {
        const valid = Number.isFinite(value) && value > 0 && (min == null || value >= min) && (max == null || value <= max);
        input.setAttribute('aria-invalid', String(!valid));
        input.setCustomValidity(valid ? '' : label + ': ' + range(min || 0.1, max));
        return valid;
      };
      const widthValid = validateDimension(widthInput, width, product.bounds.width_min_cm, widthMax, labels.width);
      const heightValid = validateDimension(heightInput, height, product.bounds.height_min_cm, product.bounds.height_max_cm, labels.height);
      const dimensionError = find('dimension-error');
      dimensionError.hidden = widthValid && heightValid;
      dimensionError.textContent = [widthValid ? '' : widthInput.validationMessage, heightValid ? '' : heightInput.validationMessage].filter(Boolean).join(' · ');
      if (widthValid && heightValid) { state.previewWidth = width; state.previewHeight = height; }
      const nextConfigurationKey = JSON.stringify([product.handle, state.width, state.height, state.mounting, state.choices, state.hex, state.options]);
      if (configurationKey !== nextConfigurationKey) {
        find('status').textContent = ''; find('cart-link').hidden = true;
        configurationKey = nextConfigurationKey;
      }
      if (currentQuote.valid && !requestOnly && window.NSBlackWeek) window.NSBlackWeek.render(find('price'), currentQuote.priceCents);
      else find('price').textContent = !currentQuote.valid ? '—' : !requestOnly ? currentQuote.money : labels.pricePending;
      find('price-note').textContent = !currentQuote.valid ? currentQuote.reason === 'dimensions' ? labels.dimensionsError : labels.unavailable
        : requestOnly ? labels.pricePendingHelp : (product.priceMode === 'measure' ? labels.priceNote + ' ' : '') + labels.shipping;
      find('inquiry-note').hidden = !requestOnly;
      addButton.hidden = requestOnly; inquiryButton.hidden = !requestOnly;
      const optionsInvalid = !!product.optionsConfig && !resolvedOptions?.valid;
      addButton.disabled = busy || !currentQuote.valid || hexInvalid || optionsInvalid;
      inquiryButton.disabled = busy || !currentQuote.valid || hexInvalid || optionsInvalid;
      const summary = find('summary'); summary.replaceChildren();
      const row = (key, value) => {summary.append(element('dt', '', key), element('dd', '', value));};
      row(labels.width, Number.isFinite(width) ? numberText(width) + ' cm' : '—');
      row(labels.height, Number.isFinite(height) ? numberText(height) + ' cm' : '—');
      if (!optionsConfig?.groups.some((group) => group.key === 'mounting')) row(labels.mounting, mounting.selectedOptions[0]?.textContent || '');
      (product.groups || []).forEach((group, index) => {
        const value = chosen(group, index);
        if (!value) return;
        row(group.label || group.option_name, value.consultation ? labels.hexCustom : colorTitle(value));
        if (value.consultation) row(labels.hexCode, normalizeHex(state.hex[index]) || '—');
      });
      Object.entries(resolvedOptions?.properties || {}).forEach(([key, value]) => row(key, value));
      const productUrl = new URL(product.url, global.location.origin);
      if (currentQuote.variantId) productUrl.searchParams.set('variant', currentQuote.variantId);
      find('product-link').href = productUrl.href;
      renderScene();
    }
    function buildOptions() {
      optionsHost?.replaceChildren();
      if (!optionsHost || !optionsConfig?.valid) return;
      const money = (amount) => new Intl.NumberFormat(root.dataset.locale || 'de', {style: 'currency', currency: 'EUR'}).format(amount / 100);
      optionsConfig.groups.filter((group) => group.key !== 'mounting').forEach((group) => {
        const label = element('label', 'ns3d-option-label', group.label);
        const input = element(group.type === 'text' ? 'input' : 'select');
        input.dataset.ns3dOption = group.key;
        input.required = group.required;
        if (group.type === 'text') { input.type = 'text'; input.maxLength = group.max_length; input.placeholder = group.placeholder; }
        else group.values.forEach((entry) => { const option = element('option', '', entry.label + (entry.surcharge_cents ? ' (+' + money(entry.surcharge_cents) + ')' : '')); option.value = entry.id; input.append(option); });
        input.value = state.options[group.key] ?? group.default;
        input.addEventListener(group.type === 'text' ? 'input' : 'change', () => {state.options[group.key] = input.value; update();});
        label.append(input); optionsHost.append(label);
        if (group.type !== 'text') global.NSChoiceTiles?.mount(input, {label: group.label, key: group.key});
        if (group.notice) optionsHost.append(element('p', 'ns3d-option-note', group.notice));
      });
      if (optionsConfig.regional_service) {
        const service = optionsConfig.regional_service;
        const link = element('a', 'ns3d-text-link', service.label);
        const url = new URL(root.dataset.contactUrl, global.location.origin); url.searchParams.set('product', product.title); url.searchParams.set('service', 'Montageservice');
        link.href = url.href; optionsHost.append(element('p', 'ns3d-option-note', service.notice), link);
      }
    }
    function buildColors() {
      colorsHost.replaceChildren();
      (product.groups || []).forEach((group, index) => {
        const fieldset = element('fieldset', 'ns3d-color-group');
        const legend = element('legend', '', group.label || group.option_name);
        const selectedName = element('span', 'ns3d-color-selected');
        selectedName.setAttribute('data-ns-3d-color-selected', '');
        selectedName.setAttribute('aria-live', 'polite');
        legend.append(selectedName); fieldset.append(legend);
        const chips = element('div', 'ns3d-swatches'); chips.setAttribute('role', 'radiogroup'); chips.setAttribute('aria-label', group.label || group.option_name);
        const buttons = [];
        let picker, codeInput, colorInput, error;
        const sync = () => {
          selectedName.textContent = selectionText(chosen(group, index), index);
          buttons.forEach((button, valueIndex) => {
            const active = state.choices[index] === valueIndex;
            button.setAttribute('aria-checked', String(active)); button.tabIndex = active ? 0 : -1;
            if (group.values[valueIndex].consultation) {
              const hex = normalizeHex(state.hex[index]);
              button.querySelector('.ns3d-swatch-code').textContent = hex || labels.hexCode;
              button.querySelector('.ns3d-swatch-sample').style.background = hex || 'conic-gradient(#ed6a5a,#ffd166,#06d6a0,#118ab2,#b565d8,#ed6a5a)';
            }
          });
          if (!picker) return;
          const active = chosen(group, index)?.consultation === true;
          picker.hidden = !active; codeInput.disabled = !active; colorInput.disabled = !active; codeInput.required = active;
          const hex = normalizeHex(state.hex[index]);
          codeInput.setCustomValidity(active && !hex ? labels.hexInvalid : '');
          codeInput.setAttribute('aria-invalid', String(active && !hex)); error.hidden = !active || !!hex;
          if (hex) colorInput.value = hex;
        };
        (group.values || []).forEach((value, valueIndex) => {
          const button = element('button', 'ns3d-swatch'); button.type = 'button'; button.setAttribute('role', 'radio');
          button.setAttribute('aria-label', value.consultation ? labels.hexCustom : colorTitle(value));
          const sample = element('span', 'ns3d-swatch-sample'); sample.setAttribute('aria-hidden', 'true');
          const frameGroup = ['Rahmenfarbe', 'Akssesuar', 'Profilfarbe'].includes(group.option_name);
          const structure = state.options.fabric_structure || (product.handle === 'plissee-100-blickdicht' ? 'honeycomb' : 'pleated');
          const sourceImage = value.media_by_structure?.[structure]?.url || value.image_url;
          if (sourceImage && !frameGroup) {
            const img = element('img'); img.src = sourceImage; img.alt = ''; img.loading = 'lazy'; img.width = 120; img.height = 90;
            if (group.option_name === 'Stofffarbe') {img.dataset.ns3dFabricGroup = index; img.dataset.ns3dFabricImage = valueIndex;}
            sample.append(img);
          } else if (frameGroup && !value.consultation && global.NSChoiceTiles) {
            sample.append(global.NSChoiceTiles.illustration('profile_color', value.name, value.color));
          } else sample.style.background = value.color || 'conic-gradient(#ed6a5a,#ffd166,#06d6a0,#118ab2,#b565d8,#ed6a5a)';
          button.append(sample, element('span', 'ns3d-swatch-name', value.consultation ? labels.hexCustom : colorTitle(value)));
          if (value.consultation) button.append(element('span', 'ns3d-swatch-code', labels.hexCode));
          button.addEventListener('click', () => { state.choices[index] = valueIndex; sync(); update(); });
          button.addEventListener('keydown', (event) => {
            let next;
            if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = (valueIndex + 1) % group.values.length;
            else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = (valueIndex + group.values.length - 1) % group.values.length;
            else if (event.key === 'Home') next = 0; else if (event.key === 'End') next = group.values.length - 1;
            if (next === undefined) return;
            event.preventDefault(); buttons[next].click(); buttons[next].focus();
          });
          buttons.push(button); chips.append(button);
        });
        fieldset.append(chips);
        if (group.values?.some((value) => value.consultation)) {
          picker = element('div', 'ns3d-hex-picker');
          const colorLabel = element('label', '', labels.hexSelect), codeLabel = element('label', '', labels.hexCode);
          colorInput = element('input'); colorInput.type = 'color'; colorInput.value = normalizeHex(state.hex[index]) || '#114232';
          codeInput = element('input'); codeInput.type = 'text'; codeInput.value = state.hex[index] || '#114232'; codeInput.placeholder = '#114232'; codeInput.maxLength = 7; codeInput.spellcheck = false; codeInput.autocomplete = 'off';
          codeInput.dataset.ns3dHexCode = ''; colorInput.dataset.ns3dHexColor = '';
          state.hex[index] = codeInput.value;
          colorLabel.append(colorInput); codeLabel.append(codeInput); error = element('p', 'ns3d-inline-error', labels.hexInvalid); error.setAttribute('role', 'alert');
          picker.append(colorLabel, codeLabel, error); fieldset.append(picker);
          colorInput.addEventListener('input', () => {state.hex[index] = colorInput.value.toUpperCase(); codeInput.value = state.hex[index]; sync(); update();});
          codeInput.addEventListener('input', () => {state.hex[index] = codeInput.value; sync(); update();});
          codeInput.addEventListener('change', () => {const hex = normalizeHex(codeInput.value); if (hex) {state.hex[index] = hex; codeInput.value = hex;} sync(); update();});
        }
        colorsHost.append(fieldset); sync();
      });
    }
    function selectProduct() {
      product = productByHandle.get(select.value) || products[0];
      const restrictedOptions = global.NSProductOptions?.restrictToProduct(product.optionsConfig, product.handle) || product.optionsConfig;
      optionsConfig = restrictedOptions ? global.NSProductOptions?.normalize(restrictedOptions) : null;
      const mountingGroup = optionsConfig?.groups.find((group) => group.key === 'mounting');
      if (mountingGroup) product.mounting = mountingGroup.values.map((value) => ({key: value.id, label: value.label + (value.surcharge_cents ? ' (+' + new Intl.NumberFormat(root.dataset.locale || 'de', {style: 'currency', currency: 'EUR'}).format(value.surcharge_cents / 100) + ')' : '')}));
      state = saved.get(product.handle) || {width: product.defaults.width, height: product.defaults.height, mounting: mountingGroup?.default || product.mounting[0].key, choices: (product.groups || []).map(() => 0), hex: {}, options: Object.fromEntries((optionsConfig?.groups || []).map((group) => [group.key, group.default]))};
      if (product.kind === 'pleated') {
        state.mounting = 'screw'; state.options.mounting = 'screw';
        const structureGroup = optionsConfig?.groups.find((group) => group.key === 'fabric_structure');
        if (product.handle !== 'plissee-100-blickdicht' || !structureGroup?.values.some((value) => value.id === state.options.fabric_structure)) {
          state.options.fabric_structure = product.handle === 'plissee-100-blickdicht' ? 'honeycomb' : 'pleated';
        }
      }
      saved.set(product.handle, state); select.value = product.handle;
      let pickerImage = find('picker-image');
      const pickerTitle = find('picker-title'), pickerSummary = find('picker')?.querySelector('[data-ns-3d-picker-current]');
      if (!pickerImage && product.image && pickerSummary) {
        pickerImage = element('img', 'ns3d-picker-thumb'); pickerImage.setAttribute('data-ns-3d-picker-image', '');
        pickerImage.width = 90; pickerImage.height = 90; pickerSummary.prepend(pickerImage);
      }
      if (pickerImage) {
        pickerImage.hidden = !product.image;
        pickerSummary?.classList.remove('ns3d-image-unavailable');
        if (product.image) {
          pickerImage.removeAttribute('srcset'); pickerImage.alt = product.title; pickerImage.decoding = 'async';
          pickerSummary?.classList.add('ns3d-image-loading');
          pickerImage.onload = () => {pickerSummary?.classList.remove('ns3d-image-loading', 'ns3d-image-unavailable');};
          pickerImage.onerror = () => {pickerImage.hidden = true; pickerSummary?.classList.remove('ns3d-image-loading'); pickerSummary?.classList.add('ns3d-image-unavailable');};
          pickerImage.src = product.image;
          if (pickerImage.complete && pickerImage.naturalWidth > 0) pickerImage.onload();
        }
      }
      if (pickerTitle) pickerTitle.textContent = product.title;
      root.querySelectorAll('[data-ns-3d-pick]').forEach((button) => {const active = button.getAttribute('data-ns-3d-pick') === product.handle; button.setAttribute('aria-checked', String(active)); button.tabIndex = active ? 0 : -1;});
      widthInput.value = state.width; heightInput.value = state.height;
      mounting.replaceChildren(); product.mounting.forEach((entry) => {const option = element('option', '', entry.label); option.value = entry.key; mounting.append(option);});
      mounting.value = state.mounting;
      global.NSChoiceTiles?.mount(mounting, {label: labels.mounting, key: 'mounting'});
      find('model-title').textContent = product.title; find('summary-title').textContent = product.title;
      find('features').innerHTML = product.featuresHtml || ''; find('properties').innerHTML = product.propertiesHtml || '';
      find('property-link').href = product.url;
      find('status').textContent = ''; find('cart-link').hidden = true;
      buildColors(); buildOptions(); update();
    }
    select.addEventListener('change', selectProduct);
    root.querySelectorAll('[data-ns-3d-pick]').forEach((button) => button.addEventListener('click', () => {
      if (busy) return;
      select.value = button.getAttribute('data-ns-3d-pick'); selectProduct();

    }));
    root.querySelectorAll('[data-ns-3d-pick]').forEach((button, index, buttons) => button.addEventListener('keydown', (event) => {
      let next;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % buttons.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index + buttons.length - 1) % buttons.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = buttons.length - 1;
      if (next === undefined) return;
      event.preventDefault(); buttons[next].click(); buttons[next].focus();
    }));
    [widthInput, heightInput].forEach((input) => input.addEventListener('input', update));
    mounting.addEventListener('change', update);
    find('opening').addEventListener('input', (event) => {find('opening-value').textContent = event.target.value + ' %'; renderScene();});
    root.querySelectorAll('[data-ns-3d-placement]').forEach((button) => button.addEventListener('click', () => {
      placement = button.getAttribute('data-ns-3d-placement') === 'free' ? 'free' : 'room';
      root.querySelectorAll('[data-ns-3d-placement]').forEach((entry) => entry.setAttribute('aria-pressed', String(entry === button)));
      renderScene();
    }));
    root.querySelectorAll('[data-ns-3d-view]').forEach((button) => button.addEventListener('click', () => {
      root.querySelectorAll('[data-ns-3d-view]').forEach((entry) => entry.setAttribute('aria-pressed', String(entry === button))); scene?.view(button.getAttribute('data-ns-3d-view'));
    }));
    root.querySelectorAll('[data-ns-3d-zoom]').forEach((button) => button.addEventListener('click', () => scene?.zoom(Number(button.getAttribute('data-ns-3d-zoom')))));
    find('reset').addEventListener('click', () => {
      scene?.reset(); find('opening').value = 0; find('opening-value').textContent = '0 %';
      root.querySelectorAll('[data-ns-3d-view]').forEach((entry) => entry.setAttribute('aria-pressed', String(entry.getAttribute('data-ns-3d-view') === 'perspective'))); renderScene();
    });
    const validateConfiguration = () => {
      if (form.reportValidity() && currentQuote.valid) return true;
      find('status').textContent = labels.configurationInvalid;
      return false;
    };
    inquiryButton.addEventListener('click', () => {
      update(); if (!validateConfiguration() || inquiryButton.disabled) return;
      const url = new URL(root.dataset.contactUrl, global.location.origin);
      url.searchParams.set('product', product.title);
      url.searchParams.set('configuration', Object.entries(properties()).map(([key, value]) => key + ': ' + value).join('\n'));
      global.location.assign(url.href);
    });
    form.addEventListener('submit', async (event) => {
      event.preventDefault(); if (busy) return; update();
      if (!validateConfiguration() || addButton.disabled || addButton.hidden) return;
      const quantity = Number(find('quantity').value);
      if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 99) return;
      const order = {id: currentQuote.variantId, quantity, properties: properties()};
      busy = true; update(); addButton.textContent = labels.adding; find('status').textContent = '';
      form.setAttribute('aria-busy', 'true');
      const lockedControls = [...form.querySelectorAll('input, select, button')].map((control) => [control, control.disabled]);
      lockedControls.forEach(([control]) => {control.disabled = true;});
      let cartMessage = labels.cartError;
      try {
        const response = await fetch(window.NSBlackWeek?.url?.(root.dataset.addUrl) || root.dataset.addUrl, {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({items: [order]})});
        const result = await response.json().catch(() => null);
        if (!response.ok || !result) {
          if (typeof result?.description === 'string') cartMessage = result.description;
          throw new Error(cartMessage);
        }
        find('status').textContent = labels.added; find('cart-link').hidden = false;
        document.dispatchEvent(new CustomEvent('ns:cart-updated', {bubbles: true, detail: {item: result.items?.[0] || result, open: true}}));
      } catch (error) { find('status').textContent = cartMessage; }
      finally {
        lockedControls.forEach(([control, disabled]) => {control.disabled = disabled;});
        form.setAttribute('aria-busy', 'false'); busy = false; addButton.textContent = labels.add; update();
      }
    });
    const requested = new URLSearchParams(global.location.search).get('product');
    if (productByHandle.has(requested)) select.value = requested;
    selectProduct();
    if (root.dataset.nsPhotoMatrixUrl && global.NSPlisseePhotos) {
      global.NSPlisseePhotos.load(root.dataset.nsPhotoMatrixUrl).then((catalog) => {
        photoMatrix = catalog;
        if (catalog && root.isConnected && product) renderScene();
      });
    }
  }
  function boot(scope) { scope.querySelectorAll('[data-ns-3d-configurator]').forEach(initialize); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document)); else boot(document);
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {event.target.querySelectorAll('[data-ns-3d-configurator]').forEach((root) => {instances.get(root)?.destroy(); instances.delete(root);});});
})(window);

